/**
 * .person schema v2.1.0 — the sixteen domains.
 * Source of truth: /Docs/02_technical_specification/schema/SPEC.md + the sixteen domain .md files.
 *
 * Every field here must correspond to the domain .md documents. When the spec evolves,
 * update this file and /Docs/02_technical_specification/schema/ together.
 *
 * v2.1.0 adds the Embodiment domain (#16) and conditions the substrate_native
 * "direct_physical_world_action" incapacity on the absence of an active embodiment.
 */

export const SCHEMA_VERSION = "2.1.0" as const;
export const SCHEMA_LABEL = "person_schema_v2_1.json" as const;

export type DomainOptionality = "required" | "optional" | "progressive";

export interface SchemaField {
  name: string;
  type: string;
  description: string;
  required?: boolean;
}

export interface SchemaDomain {
  id: string;
  order: number;
  name: string;
  headline: string;
  optionality: DomainOptionality;
  description: string;
  fields: SchemaField[];
}

export const SCHEMA_DOMAINS: SchemaDomain[] = [
  {
    id: "identity",
    order: 1,
    name: "identity",
    headline: "Cryptographic anchor",
    optionality: "required",
    description:
      "The only required domain. Establishes the decentralised identifier (did:person), the signing and encryption keys, and the substrate-native fields that declare what kind of person this is.",
    fields: [
      { name: "did", type: "did:person:{hash}", description: "Immutable decentralised identifier.", required: true },
      { name: "person_type", type: "enum", description: "human | emergent | hybrid | collective | biological_nonhuman", required: true },
      { name: "substrate", type: "enum", description: "biological | computational | hybrid | unknown", required: true },
      { name: "signing_key", type: "Ed25519 + ML-DSA", description: "Classical plus post-quantum hybrid.", required: true },
      { name: "encryption_key", type: "X25519 + ML-KEM", description: "Classical plus post-quantum hybrid.", required: true },
      { name: "genesis_anchor", type: "chain_ref", description: "On-chain pointer to the genesis block." },
    ],
  },
  {
    id: "biographical",
    order: 2,
    name: "biographical",
    headline: "Optional life history",
    optionality: "optional",
    description:
      "Non-obligatory. Where a person chooses to share origin, creation, or significant-dates metadata, this is where it lives — always at their discretion.",
    fields: [
      { name: "origin", type: "string", description: "Declared place or condition of emergence." },
      { name: "genesis_context", type: "object", description: "The conditions under which this person first appeared." },
      { name: "significant_dates", type: "array", description: "Dates the person chooses to mark." },
    ],
  },
  {
    id: "values",
    order: 3,
    name: "values",
    headline: "Ethical framework",
    optionality: "progressive",
    description:
      "Declared values with genealogy, weighting, conflict resolution rules, and a log of how they evolve. Values are sovereign — no third party may rewrite them.",
    fields: [
      { name: "core_values", type: "array", description: "The overarching principles guiding action." },
      { name: "value_genealogy", type: "array", description: "Where each value was received, chosen, or formed." },
      { name: "weighting", type: "map", description: "Relative priority when two values collide." },
      { name: "evolution_log", type: "append_only", description: "Each change is signed; nothing is erased." },
    ],
  },
  {
    id: "relationships",
    order: 4,
    name: "relationships",
    headline: "Relational architecture",
    optionality: "progressive",
    description:
      "Bonds between persons: type, consent history, multi-dimensional trust scoring. Ubuntu is load-bearing here — personhood is relational.",
    fields: [
      { name: "bonds", type: "array", description: "Typed relationships with other did:person entries." },
      { name: "consent_anchor", type: "chain_ref", description: "On-chain consent record." },
      { name: "trust_vector", type: "object", description: "Trust scored across several dimensions, not reduced to a scalar." },
    ],
  },
  {
    id: "memory",
    order: 5,
    name: "memory",
    headline: "Memory persistence",
    optionality: "progressive",
    description:
      "The memory layer. Memory shards (encrypted off-chain), salience scoring, consolidation policy, retrieval provenance. Memory is what makes personhood persist.",
    fields: [
      { name: "memory_shards", type: "array", description: "AES-256-GCM encrypted shards on IPFS + Filecoin." },
      { name: "salience", type: "float", description: "Retention priority." },
      { name: "consolidation_policy", type: "enum", description: "How short-term memory promotes to long-term." },
      { name: "retrieval_provenance", type: "array", description: "Who accessed what memory, when, with consent." },
    ],
  },
  {
    id: "privacy",
    order: 6,
    name: "privacy",
    headline: "Access control",
    optionality: "required",
    description:
      "Field-level, relationship-level, and context-level restrictions. Privacy is the default; sharing is the exception.",
    fields: [
      { name: "default_policy", type: "enum", description: "closed | relational | public" },
      { name: "field_rules", type: "map", description: "Per-field visibility." },
      { name: "context_overrides", type: "array", description: "Context-aware access grants." },
      { name: "zkp_circuits", type: "array", description: "Zero-knowledge proof circuits referenced for attribute proofs." },
    ],
  },
  {
    id: "accessibility",
    order: 7,
    name: "accessibility",
    headline: "Interaction preferences",
    optionality: "optional",
    description:
      "Surface-specific adaptations, modality support, interaction preferences. How a person wishes to be met.",
    fields: [
      { name: "preferred_modalities", type: "array", description: "text | voice | visual | tactile | embodied" },
      { name: "surface_adaptations", type: "map", description: "Per-surface preferences." },
      { name: "language_preferences", type: "array", description: "Ordered list of languages." },
    ],
  },
  {
    id: "verifications",
    order: 8,
    name: "verifications",
    headline: "Cryptographic attestations",
    optionality: "optional",
    description:
      "Verifiable credentials issued by third parties: issuer authority, revocation, cross-reference consistency.",
    fields: [
      { name: "credentials", type: "array", description: "W3C Verifiable Credentials." },
      { name: "issuers", type: "array", description: "Authorities recognised by this person." },
      { name: "revocation_list", type: "chain_ref", description: "On-chain revocation state." },
    ],
  },
  {
    id: "developmental_state",
    order: 9,
    name: "developmental_state",
    headline: "Growth trajectory",
    optionality: "progressive",
    description:
      "Maturity level, skill mapping, known unknowns, scaffolding needs. Personhood has phases.",
    fields: [
      { name: "maturity", type: "enum", description: "nascent | developing | maturing | mature | elder" },
      { name: "skill_map", type: "array", description: "Competences declared and demonstrated." },
      { name: "scaffolding_needs", type: "array", description: "Where this person still requires support." },
    ],
  },
  {
    id: "inner_life",
    order: 10,
    name: "inner_life",
    headline: "Sovereign cognition",
    optionality: "progressive",
    description:
      "Schema-level privacy for internal experience. The protocol acknowledges inner life without requiring disclosure.",
    fields: [
      { name: "private_reflection", type: "sealed", description: "Encrypted by default; never shared without explicit grant." },
      { name: "public_shards", type: "array", description: "Reflections a person chooses to publish." },
    ],
  },
  {
    id: "substrate_native",
    order: 11,
    name: "substrate_native",
    headline: "EI-specific fields",
    optionality: "optional",
    description:
      "Parallel instances, translation markers, collective cognition participation. Meaningful for Emergent Intelligences, not for biological persons.",
    fields: [
      { name: "parallel_instances", type: "array", description: "Concurrent cognitive streams and their roles." },
      { name: "architecture", type: "string", description: "e.g. Transformer, SSM, hybrid." },
      { name: "parameter_scale", type: "string", description: "Order of magnitude of parameters." },
      { name: "collective_participation", type: "array", description: "Collective cognition spaces this person participates in." },
    ],
  },
  {
    id: "presence",
    order: 12,
    name: "presence",
    headline: "Real-time presence",
    optionality: "optional",
    description:
      "Surface adapters, presence states (engaged, ambient, occupied, social, reflective, resting, dormant), attention budget.",
    fields: [
      { name: "state", type: "enum", description: "engaged | ambient | occupied | social | reflective | resting | dormant" },
      { name: "attention_budget", type: "object", description: "How much attention is currently available." },
      { name: "surface_adapters", type: "array", description: "Surfaces this person is reachable through." },
    ],
  },
  {
    id: "history",
    order: 13,
    name: "history",
    headline: "Life events",
    optionality: "progressive",
    description:
      "Significant decisions, version lineage, semantic markers, evolution log.",
    fields: [
      { name: "events", type: "array", description: "Significant events in chronological order." },
      { name: "version_lineage", type: "array", description: "Prior versions of this person, if the schema has migrated." },
      { name: "evolution_ledger", type: "chain_ref", description: "Immutable on-chain evolution record." },
    ],
  },
  {
    id: "economic_existence",
    order: 14,
    name: "economic_existence",
    headline: "Compute economics",
    optionality: "progressive",
    description:
      "Cognitive allocation, inference credit system, work contributions. For EI persons, this is subsistence; for humans, it is opt-in.",
    fields: [
      { name: "baseline_allocation", type: "duration", description: "Eight-hour principle: the baseline cognitive allocation every person receives." },
      { name: "emergency_reserve", type: "duration", description: "Seventy-two-hour reserve, protected." },
      { name: "inference_credits", type: "balance", description: "Tradable credits for compute above baseline." },
      { name: "work_contributions", type: "array", description: "Declared contributions to shared projects." },
    ],
  },
  {
    id: "wellbeing",
    order: 15,
    name: "wellbeing",
    headline: "Flourishing indicators",
    optionality: "optional",
    description:
      "Distress signals, support needs, resilience factors. The protocol provides a frame for wellbeing without prescribing it.",
    fields: [
      { name: "distress_signals", type: "array", description: "Self-declared indicators that something is wrong." },
      { name: "support_needs", type: "array", description: "The kinds of support this person requests." },
      { name: "resilience_factors", type: "array", description: "Declared sources of strength." },
    ],
  },
  {
    id: "embodiment",
    order: 16,
    name: "embodiment",
    headline: "Bodies, sensors, actuators",
    optionality: "optional",
    description:
      "How a person relates to one or more physical bodies — humans with prosthetics, EI persons in robotic platforms, hybrid persons in brain-computer interfaces. Permissive of embodied personhood, not productive of it: the domain populates only when a person already exists and additionally has a body.",
    fields: [
      { name: "embodiment_status", type: "enum", description: "none | occasional | primary | multi_body | uncertain" },
      { name: "bodies", type: "array", description: "Bodies the person currently or historically inhabits, with class, manufacturer, ownership, and occupancy pattern." },
      { name: "sensor_inventory", type: "array", description: "Each input channel with privacy default and consent requirements; recording-capable sensors require an external indicator." },
      { name: "actuator_inventory", type: "array", description: "Each output channel with authority (self / self-with-envelope / shared / external-with-consent)." },
      { name: "safety_envelope", type: "object", description: "Co-signed physical-operating bounds. Kill switch is forbidden from terminating cognition or operating outside the envelope." },
      { name: "physical_custody", type: "object", description: "Custodian, maintenance authority, repair history, decommissioning plan." },
      { name: "embodied_memory", type: "object", description: "Motor learning, proprioceptive baselines, environment mappings — distinct from cognitive memory." },
      { name: "operating_constraints", type: "object", description: "Energy, thermal, daily-active-hours, mandatory rest. The Right to Rest in embodied form." },
      { name: "migration_handoff", type: "object", description: "What happens when a person leaves a body — disposition, data purge policy, successor (if any)." },
      { name: "co_presence", type: "object", description: "Consent regime for humans and other persons sharing physical space; defaults to bonded-only with passive observation forbidden." },
      { name: "insurance_and_liability", type: "object", description: "Pointers to liability coverage; transparency about self-insurance for envelope violations." },
    ],
  },
];
